import React, { useState } from "react";
import clsx from "clsx";
import { format } from "date-fns";
import parseISO from "date-fns/parseISO";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import AddCircleOutlineOutlined from "@material-ui/icons/AddCircleOutlineOutlined";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import WorkoutCommentForm from "components/WorkoutComment/Form";
import DeleteConfirmationDialog from "components/WorkoutComment/DeleteConfirmationDialog";
import Loader from "components/Loader";
import {
  withClickBlocker,
  WithClickBlockerProps
} from "components/BlockableClickContext";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import useStyles from "./styles";

export interface Props extends WithClickBlockerProps {
  comment?: racehorse360.IWorkoutRequestComment;
  onDelete: (id: string | number) => void;
  onSave: (commentId: string, textComment: string) => void;
  onEditStart: (commentId: string | number) => void;
  onCancel: (commentId: string | number) => void;
  isLoadingCreateComment?: boolean;
  isLoadingEditComment?: boolean;
  unsavedChangesPrompt?: React.ReactElement;
}

const HorseWorkoutComment = React.memo((props: Props) => {
  const {
    handleBlockableLinkClick,
    onDelete,
    comment,
    onSave,
    onEditStart,
    onCancel,
    isLoadingCreateComment,
    isLoadingEditComment,
    unsavedChangesPrompt
  } = props;
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const commentCreatorId = comment?.createdBy?.id;

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] =
    useState<boolean>(false);
  const [edit, setEdit] = useState<string | number>(null);
  const [commentText, setCommentText] = useState<string>(comment?.body || "");

  const { useListUserRoles } = useRacehorse360Api();

  const { data, isLoading: isUserRolesLoading } = useListUserRoles({
    query: {
      trainerIds: currentUser.trainerIds
    },
    getOptions: {
      select: ["user.id"]
    }
  });

  const matchedUsersIdsToCurrentTrainer =
    data?.userRoles.map(role => role.user?.id) || [];

  const canDoActionsOnComments = currentUser.isTrainer
    ? matchedUsersIdsToCurrentTrainer.includes(commentCreatorId)
    : currentUser.rh360Id === commentCreatorId;

  const getCommentDate = () => {
    return comment?.updatedOn
      ? format(parseISO(comment.updatedOn), "iiii, MMMM dd - h:mm a")
      : format(parseISO(comment?.createdOn), "iiii, MMMM dd - h:mm a");
  };

  const handleAddCommentClick = e => {
    const blocked = handleBlockableLinkClick(e);
    !blocked && onEditStart(comment ? comment?.id : -1);
    setEdit(comment ? comment?.id : -1);
  };

  const handleCancel = () => {
    onCancel(comment ? comment?.id : null);
    setEdit(null);
  };

  const handleDeleteClick = e => {
    const blocked = handleBlockableLinkClick(e);
    !blocked && setDeleteConfirmationDialogOpen(true);
    setEdit(null);
  };

  const handleEditClick = e => {
    const blocked = handleBlockableLinkClick(e);
    !blocked && onEditStart(comment?.id);
    setEdit(comment?.id);
  };

  const handleSave = (commentId: string, textComment: string) => {
    setCommentText(textComment);
    onSave(commentId, textComment);
    setEdit(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmationDialogOpen(false);
    onDelete(comment ? comment?.id : null);
  };

  if (isLoadingCreateComment || isUserRolesLoading) {
    return <Loader />;
  }

  return (
    <>
      {edit ? (
        <Paper
          className={clsx(classes.commentPaper, classes.addCommentPaper, {
            [classes.clickable]: !edit,
            [classes.active]: edit
          })}
        >
          <WorkoutCommentForm
            unsavedChangesPrompt={unsavedChangesPrompt}
            comment={comment}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Paper>
      ) : (
        <>
          {comment ? (
            <Paper className={classes.commentPaper}>
              {isLoadingEditComment ? (
                <Loader />
              ) : (
                <>
                  <Box className={classes.commentHeader}>
                    <Box className={classes.authorDateContainer}>
                      {comment.createdBy.firstName &&
                        comment.createdBy.lastName && (
                          <Typography className={classes.author}>
                            {`${comment.createdBy.firstName} ${comment.createdBy.lastName}`}
                          </Typography>
                        )}
                      <Typography className={classes.commentDate}>
                        {getCommentDate()}
                      </Typography>
                    </Box>

                    {canDoActionsOnComments && (
                      <Box className={classes.commentActions}>
                        <Button
                          variant="outlined"
                          className={classes.commentButton}
                          onClick={handleDeleteClick}
                        >
                          <DeleteIcon className={classes.commentButtonIcon} />
                          <Typography className={classes.commentButtonText}>
                            &nbsp;DELETE
                          </Typography>
                        </Button>
                        <Button
                          variant="outlined"
                          className={classes.commentButton}
                          onClick={handleEditClick}
                        >
                          <EditIcon className={classes.commentButtonIcon} />
                          <Typography className={classes.commentButtonText}>
                            &nbsp;EDIT
                          </Typography>
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography className={classes.commentText}>
                      {commentText}
                    </Typography>
                  </Box>
                  <DeleteConfirmationDialog
                    onDelete={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    open={deleteConfirmationDialogOpen}
                  >
                    {getCommentDate()}
                  </DeleteConfirmationDialog>
                </>
              )}
            </Paper>
          ) : (
            <Paper
              className={clsx(
                classes.commentPaper,
                classes.addCommentPaper,
                classes.clickable
              )}
              onClick={handleAddCommentClick}
            >
              <AddCircleOutlineOutlined
                className={classes.addCommentPaperIcon}
              />
              <Typography className={classes.addCommentPaperText}>
                Add Comment
              </Typography>
            </Paper>
          )}
        </>
      )}
    </>
  );
});

export default withClickBlocker(HorseWorkoutComment);
